import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { CompanyImageType, CompanyImageUploadData } from '../../../../types/models';

interface ImageUploaderProps {
  open: boolean;
  onClose: () => void;
  onUpload: (uploadData: CompanyImageUploadData) => Promise<void>;
}

const ImageTypeColors: Record<CompanyImageType, 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  [CompanyImageType.LOGO]: 'success',
  [CompanyImageType.HEADER]: 'success',
  [CompanyImageType.SIGNATURE]: 'warning',
  [CompanyImageType.WATERMARK]: 'info',
  [CompanyImageType.LETTERHEAD]: 'success',
  [CompanyImageType.OTHER]: 'secondary'
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  open,
  onClose,
  onUpload
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState(''); // Changed from imageName
  const [imageType, setImageType] = useState<CompanyImageType>(CompanyImageType.LOGO);
  const [description, setDescription] = useState(''); // Added description field
  const [tags, setTags] = useState(''); // Added tags field
  const [preview, setPreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    //console.log('🔥 ImageUploader dialog open state changed:', open);
    if (open) {
      // Only reset when opening, not when preview changes
      //console.log('🔥 Resetting form state because dialog opened');
      setSelectedFile(null);
      setFileName('');
      setDescription('');
      setTags('');
      setImageType(CompanyImageType.LOGO);
      setPreview(null);
      setDragOver(false);
      setUploading(false);
      setError(null);
    }
  }, [open]); // Removed preview from dependencies

  // Separate effect for cleanup when dialog closes
  React.useEffect(() => {
    return () => {
      // Clean up preview URL when component unmounts or dialog closes
      if (!open && preview) {
        //console.log('🔥 Cleaning up preview URL:', preview);
        URL.revokeObjectURL(preview);
      }
    };
  }, [open, preview]);

  // Validate file
  const validateFile = (file: File): string | null => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload JPG, PNG, SVG, or WebP images only.';
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'File size too large. Please upload images smaller than 10MB.';
    }

    return null;
  };

  // Handle file selection
  const handleFileSelect = (file: File) => {
    //console.log('🔥 handleFileSelect called with file:', file);
    const validationError = validateFile(file);
    //console.log('🔥 Validation result:', validationError);
    if (validationError) {
      //console.log('🔥 File validation failed:', validationError);
      setError(validationError);
      return;
    }

    //console.log('🔥 File validation passed, setting selected file');
    setSelectedFile(file);
    setError(null);

    // Auto-generate name from filename (without extension)
    const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
    //console.log('🔥 Generated image name:', nameWithoutExt);
    setFileName(nameWithoutExt);

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    //console.log('🔥 Created preview URL:', previewUrl);
    setPreview(previewUrl);
  };

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //console.log('🔥 File input change event triggered');
    //console.log('🔥 Event target:', event.target);
    //console.log('🔥 Files:', event.target.files);
    const file = event.target.files?.[0];
    //console.log('🔥 Selected file:', file);
    if (file) {
      //console.log('🔥 Calling handleFileSelect with file:', file.name);
      handleFileSelect(file);
    } else {
      //console.log('🔥 No file selected');
    }
  };

  // Handle drag and drop
  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  // Remove selected file
  const handleRemoveFile = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setSelectedFile(null);
    setPreview(null);
    setFileName('');
    setDescription('');
    setTags('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedFile || !fileName.trim()) {
      setError('Please select a file and enter a name');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploadData: CompanyImageUploadData = {
        fileName: fileName.trim(),
        imageType,
        file: selectedFile,
        description: description.trim() || undefined,
        tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
      };

      await onUpload(uploadData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setImageType(event.target.value as CompanyImageType);
  };

  const handleBrowseClick = () => {
    //console.log('🔥 Browse button clicked, fileInputRef:', fileInputRef.current);
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Company Image</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* File Upload Area */}
          {!selectedFile ? (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: dragOver ? 'action.hover' : 'transparent',
                '&:hover': {
                  borderColor: 'var(--accent-green)',
                  backgroundColor: 'action.hover'
                }
              }}
              onClick={handleBrowseClick}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Drop your image here or click to browse
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Supports JPG, PNG, SVG, and WebP files up to 10MB
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                sx={{ 
                  mt: 2,
                  borderColor: 'var(--accent-green)',
                  color: 'var(--accent-green)',
                  '&:hover': {
                    borderColor: 'var(--accent-green)',
                    backgroundColor: 'rgba(46, 125, 77, 0.1)'
                  }
                }}
              >
                Choose File
              </Button>
            </Box>
          ) : (
            /* File Preview */
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Selected File
                </Typography>
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveFile}
                  color="error"
                >
                  Remove
                </Button>
              </Box>
              
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1
                }}
              >
                {preview && (
                  <img
                    src={preview}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                )}
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" gutterBottom>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(selectedFile.size)} • {selectedFile.type}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Form Fields */}
          <TextField
            fullWidth
            label="Image Name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Enter a descriptive name for this image"
            disabled={uploading}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--accent-green)'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--accent-green)'
              }
            }}
          />

          <TextField
            fullWidth
            label="Description (Optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of the image"
            disabled={uploading}
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--accent-green)'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--accent-green)'
              }
            }}
          />

          <TextField
            fullWidth
            label="Tags (Optional)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Enter tags separated by commas (e.g., logo, branding, official)"
            disabled={uploading}
            helperText="Tags help organize and search for images"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--accent-green)'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--accent-green)'
              }
            }}
          />

          <FormControl 
            fullWidth 
            disabled={uploading}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--accent-green)'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--accent-green)'
              }
            }}
          >
            <InputLabel>Image Type</InputLabel>
            <Select
              value={imageType}
              label="Image Type"
              onChange={handleTypeChange}
            >
              {Object.values(CompanyImageType).map((type) => (
                <MenuItem key={type} value={type}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={type}
                      size="small"
                      color={ImageTypeColors[type]}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Upload Progress */}
          {uploading && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uploading image...
              </Typography>
              <LinearProgress 
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'var(--accent-green)'
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedFile || !fileName.trim() || uploading}
          sx={{
            backgroundColor: 'var(--accent-green)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--accent-green)',
              filter: 'brightness(0.9)'
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)'
            }
          }}
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
        </Button>
      </DialogActions>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </Dialog>
  );
};
